import { useMemo, useState } from 'react';
import { ChevronLeft, Download, FileUp, Search, User as UserIcon } from 'lucide-react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminUserSearch } from '@/hooks/useAdminUserSearch';
import { useUserAdminView } from '@/hooks/useUserAdminView';
import { ConnectCrmImportDialog } from '@/components/admin/ConnectCrmImportDialog';
import { ImportContactsDialog } from '@/components/contacts/import/ImportContactsDialog';
import { UserLegalConsentsCard } from '@/components/admin/UserLegalConsentsCard';
import { UserAccountAssociationCard } from '@/components/admin/UserAccountAssociationCard';
import { UserAdminActionsCard } from '@/components/admin/UserAdminActionsCard';
import type { AdminUserListRow } from '@/types/admin';

/**
 * Normalize unknown errors to a human-readable string. Supabase returns
 * `PostgrestError` as a plain object (with `message`, `details`, `hint`,
 * `code`), NOT an `Error` instance — so a naive `error instanceof Error`
 * check always swallows the real RPC message and falls back to "Unknown
 * error". Handle both shapes here.
 */
function describeError(err: unknown): string {
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  ) {
    return (err as { message: string }).message;
  }
  if (err instanceof Error) return err.message;
  return 'Unknown error.';
}

type AccountsPanelView = 'list' | 'detail';

/**
 * Sheet content for the super-admin Accounts surface. List view = debounced
 * fuzzy user search via `enterprise_sso_user_search`. Detail view =
 * per-user identity, workspaces (with role + contact count), account
 * status. Modeled on EnterprisePanel's list/detail drill-down shape.
 *
 * Part of the Connect CRM importer epic. Detail view shows identity,
 * workspaces, status, and an Imports card with the Connect CRM launch
 * button.
 *
 * Sheet open/close is owned by AdminBar via Sheet's `onOpenChange`; this
 * component is purely content. PR-2 may re-add an `onClose` prop if it
 * needs to programmatically close after a successful import.
 */
export function AccountsPanel(): JSX.Element {
  const [view, setView] = useState<AccountsPanelView>('list');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const handleSelectUser = (userId: string) => {
    setSelectedUserId(userId);
    setView('detail');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedUserId(null);
  };

  return (
    <div className="space-y-6 overflow-y-auto h-full">
      {view === 'list' ? (
        <AccountsListView onSelect={handleSelectUser} />
      ) : (
        <AccountsDetailView
          userId={selectedUserId}
          onBack={handleBackToList}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* List view                                                                  */
/* -------------------------------------------------------------------------- */

interface AccountsListViewProps {
  onSelect: (userId: string) => void;
}

function AccountsListView({ onSelect }: AccountsListViewProps): JSX.Element {
  const [search, setSearch] = useState('');
  const { data, isLoading, isError, error, debouncedQuery } = useAdminUserSearch(search);

  return (
    <>
      <SheetHeader>
        <SheetTitle>Accounts</SheetTitle>
        <SheetDescription>
          Browse and manage user accounts.
        </SheetDescription>
      </SheetHeader>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="pl-9"
          aria-label="Search users"
        />
      </div>

      <div className="space-y-2">
        {isLoading ? (
          <ListSkeleton />
        ) : isError ? (
          <EmptyState
            icon={<UserIcon className="h-8 w-8 text-destructive" />}
            title="Failed to load users"
            description={describeError(error)}
          />
        ) : !data || data.length === 0 ? (
          <EmptyState
            icon={<UserIcon className="h-8 w-8 text-muted-foreground" />}
            title="No users found"
            description={
              debouncedQuery.length > 0
                ? `Nothing matched "${debouncedQuery}".`
                : 'No users in the system yet.'
            }
          />
        ) : (
          data.map((user) => (
            <UserSearchRow
              key={user.id}
              user={user}
              onClick={() => onSelect(user.id)}
            />
          ))
        )}
      </div>
    </>
  );
}

interface UserSearchRowProps {
  user: AdminUserListRow;
  onClick: () => void;
}

function UserSearchRow({ user, onClick }: UserSearchRowProps): JSX.Element {
  const initials = useMemo(() => buildInitials(user.first_name, user.last_name, user.email), [
    user.first_name,
    user.last_name,
    user.email,
  ]);
  const displayName = formatName(user.first_name, user.last_name) ?? user.email ?? '(unknown)';
  const lastSeen = user.last_sign_in_at
    ? `Last seen: ${formatDate(user.last_sign_in_at)}`
    : 'Never signed in.';

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2 text-left hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
    >
      <Avatar className="h-9 w-9">
        <AvatarFallback className="text-xs">{initials}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{displayName}</div>
        {user.email && (
          <div className="text-xs text-muted-foreground truncate">{user.email}</div>
        )}
        <div className="text-xs text-muted-foreground truncate">{lastSeen}</div>
      </div>
    </button>
  );
}

function ListSkeleton(): JSX.Element {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
        >
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-48" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Detail view                                                                */
/* -------------------------------------------------------------------------- */

interface AccountsDetailViewProps {
  userId: string | null;
  onBack: () => void;
}

function AccountsDetailView({ userId, onBack }: AccountsDetailViewProps): JSX.Element {
  const { data, isLoading, isError, error } = useUserAdminView(userId);

  return (
    <>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
          <ChevronLeft className="h-4 w-4 mr-1" />
          All accounts
        </Button>
      </div>

      {isLoading ? (
        <DetailSkeleton />
      ) : isError ? (
        <EmptyState
          icon={<UserIcon className="h-8 w-8 text-destructive" />}
          title="Failed to load user"
          description={describeError(error)}
        />
      ) : !data ? (
        <EmptyState
          icon={<UserIcon className="h-8 w-8 text-muted-foreground" />}
          title="No user selected"
          description="Pick a user from the list to see details."
        />
      ) : (
        <div className="space-y-4">
          <UserIdentityCard user={data.user} />
          {/* account_association may be absent on responses cached before the
              get_user_admin_view migration that added it. Skip the card
              rather than crash; next refetch will populate it. */}
          {data.account_association && (
            <UserAccountAssociationCard association={data.account_association} />
          )}
          <UserWorkspacesCard workspaces={data.workspaces} />
          <UserStatusCard user={data.user} />
          <UserLegalConsentsCard userId={data.user.id} />
          <UserAdminActionsCard
            targetUserId={data.user.id}
            targetEmail={data.user.email}
            targetFirstName={data.user.first_name}
            targetLastName={data.user.last_name}
          />
          <UserImportsCard user={data.user} workspaces={data.workspaces} />
        </div>
      )}
    </>
  );
}

interface UserIdentityCardProps {
  user: NonNullable<ReturnType<typeof useUserAdminView>['data']>['user'];
}

function UserIdentityCard({ user }: UserIdentityCardProps): JSX.Element {
  const initials = buildInitials(user.first_name, user.last_name, user.email);
  const displayName = formatName(user.first_name, user.last_name) ?? user.email ?? '(unknown)';

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-4 space-y-0">
        <Avatar className="h-12 w-12">
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <CardTitle className="text-lg truncate">{displayName}</CardTitle>
          {user.email && (
            <div className="text-sm text-muted-foreground truncate">{user.email}</div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Created</dt>
          <dd>{formatDate(user.created_at)}</dd>
        </dl>
      </CardContent>
    </Card>
  );
}

interface UserWorkspacesCardProps {
  workspaces: NonNullable<ReturnType<typeof useUserAdminView>['data']>['workspaces'];
}

function UserWorkspacesCard({ workspaces }: UserWorkspacesCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Workspaces</CardTitle>
      </CardHeader>
      <CardContent>
        {workspaces.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            User has no active workspace memberships.
          </div>
        ) : (
          <div className="space-y-2">
            {workspaces.map((w) => (
              <div
                key={w.id}
                className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{w.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {w.contact_count.toLocaleString()} contact
                    {w.contact_count === 1 ? '' : 's'}
                    {' · joined '}
                    {formatDate(w.joined_at)}
                  </div>
                </div>
                <Badge variant="secondary" className="capitalize">
                  {w.role}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface UserStatusCardProps {
  user: NonNullable<ReturnType<typeof useUserAdminView>['data']>['user'];
}

function UserStatusCard({ user }: UserStatusCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Account status</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Status</dt>
          <dd>
            <Badge variant="outline" className="capitalize">
              {user.status}
            </Badge>
          </dd>
          <dt className="text-muted-foreground">Last sign-in</dt>
          <dd>{user.last_sign_in_at ? formatDate(user.last_sign_in_at) : 'Never'}</dd>
        </dl>
      </CardContent>
    </Card>
  );
}

interface UserImportsCardProps {
  user: NonNullable<ReturnType<typeof useUserAdminView>['data']>['user'];
  workspaces: NonNullable<ReturnType<typeof useUserAdminView>['data']>['workspaces'];
}

/**
 * Imports card — launchers for super-admin "import on behalf of user" flows.
 * Two paths today:
 *   1. Connect CRM importer (workspace picked inside its own dialog).
 *   2. CSV / vCard via the regular ImportContactsDialog with a
 *      workspace-override + admin-context banner (PR-4).
 *
 * Always visible to super-admins (the AccountsPanel sheet is itself
 * super-admin gated). When the user has no active workspaces both buttons
 * are disabled — there's nothing to import into.
 */
function UserImportsCard({ user, workspaces }: UserImportsCardProps): JSX.Element {
  const [connectOpen, setConnectOpen] = useState(false);
  const [csvPickerOpen, setCsvPickerOpen] = useState(false);
  const noWorkspaces = workspaces.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Imports</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setConnectOpen(true)}
            disabled={noWorkspaces}
          >
            <Download className="h-4 w-4 mr-2" />
            Import from Connect CRM
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setCsvPickerOpen(true)}
            disabled={noWorkspaces}
          >
            <FileUp className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
        </div>
        {noWorkspaces && (
          <p className="mt-2 text-xs text-muted-foreground">
            User has no active workspaces — nothing to import into.
          </p>
        )}
      </CardContent>
      {connectOpen && (
        <ConnectCrmImportDialog
          open={connectOpen}
          onOpenChange={setConnectOpen}
          user={user}
          workspaces={workspaces}
        />
      )}
      {/* Keep mounted unconditionally. Gating on csvPickerOpen would
          unmount the launcher when handleContinue closes the picker,
          destroying the ImportContactsDialog before its open transition
          can run. Radix Dialog hides itself when open=false. */}
      <AdminCsvImportLauncher
        open={csvPickerOpen}
        onOpenChange={setCsvPickerOpen}
        user={user}
        workspaces={workspaces}
      />
    </Card>
  );
}

interface AdminCsvImportLauncherProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: NonNullable<ReturnType<typeof useUserAdminView>['data']>['user'];
  workspaces: NonNullable<ReturnType<typeof useUserAdminView>['data']>['workspaces'];
}

/**
 * Two-step admin CSV launcher: pick a target workspace, then open the
 * regular ImportContactsDialog with `workspaceIdOverride` + `adminContext`.
 * The picker step is its own small dialog so we don't bolt workspace
 * selection onto ImportContactsDialog's existing 4-tab flow. When the user
 * has exactly one workspace we still render the picker (one option,
 * preselected) for consistency — the user just clicks Continue.
 */
function AdminCsvImportLauncher({
  open,
  onOpenChange,
  user,
  workspaces,
}: AdminCsvImportLauncherProps): JSX.Element {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>(
    workspaces[0]?.id ?? '',
  );
  const [importOpen, setImportOpen] = useState(false);

  const selectedWorkspace = useMemo(
    () => workspaces.find((w) => w.id === selectedWorkspaceId) ?? null,
    [workspaces, selectedWorkspaceId],
  );

  const targetUserName =
    formatName(user.first_name, user.last_name) ?? user.email ?? '(unknown)';

  const handleContinue = () => {
    if (!selectedWorkspace) return;
    onOpenChange(false);
    setImportOpen(true);
  };

  // When the import dialog closes we don't reopen the picker — admin can
  // re-launch from the Imports card if they want another round.
  const handleImportOpenChange = (next: boolean) => {
    setImportOpen(next);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import CSV on behalf of user</DialogTitle>
            <DialogDescription>
              Pick the workspace to import into. The CSV upload step opens next.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="admin-csv-workspace">Target workspace</Label>
            <Select
              value={selectedWorkspaceId}
              onValueChange={setSelectedWorkspaceId}
              disabled={workspaces.length === 0}
            >
              <SelectTrigger id="admin-csv-workspace">
                <SelectValue placeholder="Pick a workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleContinue} disabled={!selectedWorkspace}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {importOpen && selectedWorkspace && (
        <ImportContactsDialog
          open={importOpen}
          onOpenChange={handleImportOpenChange}
          workspaceIdOverride={selectedWorkspace.id}
          adminContext={{
            targetUserName,
            targetWorkspaceName: selectedWorkspace.name,
          }}
        />
      )}
    </>
  );
}

function DetailSkeleton(): JSX.Element {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center gap-4 space-y-0">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-56" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-4 w-32" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-24" />
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Shared                                                                     */
/* -------------------------------------------------------------------------- */

interface EmptyStateProps {
  icon: JSX.Element;
  title: string;
  description: string;
}

function EmptyState({ icon, title, description }: EmptyStateProps): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border py-10 text-center">
      {icon}
      <div className="text-sm font-medium">{title}</div>
      <div className="text-xs text-muted-foreground max-w-xs">{description}</div>
    </div>
  );
}

function buildInitials(
  firstName: string | null,
  lastName: string | null,
  email: string | null,
): string {
  const f = firstName?.trim()?.[0];
  const l = lastName?.trim()?.[0];
  if (f && l) return `${f}${l}`.toUpperCase();
  if (f) return f.toUpperCase();
  if (email && email.length > 0) return email[0].toUpperCase();
  return '?';
}

function formatName(firstName: string | null, lastName: string | null): string | null {
  const parts = [firstName, lastName].filter((p): p is string => !!p && p.trim().length > 0);
  return parts.length > 0 ? parts.join(' ') : null;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'PP'); // e.g. May 7, 2026
  } catch {
    return iso;
  }
}
